import React, { useEffect, useState, useRef } from "react";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import "./EnterAddress.css";
import { updateUser } from '../../../api/auth';
import { getAllCountries, getState, getCitiesInAState } from '../../../api/address';
import { setActiveComponent } from "../../../Redux/Slices/activeComponentSlice";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../Loader";
import { setUserDetails } from "../../../Redux/Slices/userDetailsSlice";
import { Snackbar, Alert } from '@mui/material';
import { getUserId, getToken } from "../../../api/index";

function EnterAddress({ signUp }) {
  const userDetails = useSelector((state) => state.userDetails?.userDetails);
  const dispatch = useDispatch();

  // Dropdown Open States
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [stateMenuOpen, setStateMenuOpen] = useState(false);
  const [cityMenuOpen, setCityMenuOpen] = useState(false);

  // Form Field States
  const [country, setCountry] = useState(userDetails?.addresses?.[0]?.country || '');
  const [state, setState] = useState(userDetails?.addresses?.[0]?.state || '');
  const [city, setCity] = useState(userDetails?.addresses?.[0]?.city || '');
  const [address, setAddress] = useState(userDetails?.addresses?.[0]?.addressLine || '');
  const [postalCode, setPostalCode] = useState(userDetails?.addresses?.[0]?.zipCode || userDetails?.addresses?.[0]?.zipcode || '');

  // API Data Lists
  const [countryFromApi, setCountryFromApi] = useState([]);
  const [stateFromApi, setStateFromApi] = useState([]);
  const [cityFromApi, setCityFromApi] = useState([]);

  // API Loading States
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const postalCodeFormats = {
    "United States": /^\d{5}(-\d{4})?$/,
    Canada: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
    India: /^\d{6}$/,
    GB: /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/,
  };

  const validatePostalCodeRegex = (code, countryName) => {
    const regex = postalCodeFormats[countryName];
    return regex ? regex.test(code) : true;
  };

  const countryDropdownRef = useRef(null);
  const stateDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);

  // Synchronize when Redux userDetails arrives or updates
  useEffect(() => {
    if (userDetails?.addresses && userDetails.addresses.length > 0) {
      const addr = userDetails.addresses[0];
      if (addr.country) setCountry(addr.country);
      if (addr.state) setState(addr.state);
      if (addr.city) setCity(addr.city);
      if (addr.addressLine) setAddress(addr.addressLine);
      if (addr.zipCode || addr.zipcode) setPostalCode(addr.zipCode || addr.zipcode);
    }
  }, [userDetails]);

  // Click outside listener for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideCountry = !countryDropdownRef.current || !countryDropdownRef.current.contains(event.target);
      const isOutsideState = !stateDropdownRef.current || !stateDropdownRef.current.contains(event.target);
      const isOutsideCity = !cityDropdownRef.current || !cityDropdownRef.current.contains(event.target);

      if (isOutsideCountry && isOutsideState && isOutsideCity) {
        setCountryMenuOpen(false);
        setStateMenuOpen(false);
        setCityMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // 1. Fetch Countries on Mount
  useEffect(() => {
    let isMounted = true;
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const data = await getAllCountries();
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (Array.isArray(data?.data)) {
          list = data.data;
        }
        if (isMounted) setCountryFromApi(list);
      } catch (err) {
        console.error("Error loading countries:", err);
      } finally {
        if (isMounted) setLoadingCountries(false);
      }
    };
    fetchCountries();
    return () => {
      isMounted = false;
    };
  }, []);

  // 2. Fetch States when Country changes
  useEffect(() => {
    if (!country) {
      setStateFromApi([]);
      return;
    }
    let isMounted = true;
    const fetchStates = async () => {
      try {
        setLoadingStates(true);
        const data = await getState(country);
        let list = [];
        if (data && Array.isArray(data.state)) {
          list = data.state;
        } else if (data && Array.isArray(data.State)) {
          list = data.State;
        } else if (Array.isArray(data)) {
          list = data;
        } else if (data?.data && Array.isArray(data.data)) {
          list = data.data;
        }
        if (isMounted) setStateFromApi(list);
      } catch (err) {
        console.error("Error loading states:", err);
        if (isMounted) setStateFromApi([]);
      } finally {
        if (isMounted) setLoadingStates(false);
      }
    };
    fetchStates();
    return () => {
      isMounted = false;
    };
  }, [country]);

  // 3. Fetch Cities when Country or State changes
  useEffect(() => {
    if (!country || !state) {
      setCityFromApi([]);
      return;
    }
    let isMounted = true;
    const fetchCities = async () => {
      try {
        setLoadingCities(true);
        const data = await getCitiesInAState({ country, state });
        let list = [];
        if (data && Array.isArray(data.cities)) {
          list = data.cities;
        } else if (data && Array.isArray(data.Cities)) {
          list = data.Cities;
        } else if (Array.isArray(data)) {
          list = data;
        } else if (data?.data && Array.isArray(data.data)) {
          list = data.data;
        }
        if (isMounted) setCityFromApi(list);
      } catch (err) {
        console.error("Error loading cities:", err);
        if (isMounted) setCityFromApi([]);
      } finally {
        if (isMounted) setLoadingCities(false);
      }
    };
    fetchCities();
    return () => {
      isMounted = false;
    };
  }, [country, state]);

  // Dropdown open toggles
  const handleCountryDrowpdownClick = () => {
    setCountryMenuOpen((prev) => !prev);
    setStateMenuOpen(false);
    setCityMenuOpen(false);
  };

  const handleStateDropdownClick = () => {
    setStateMenuOpen((prev) => !prev);
    setCountryMenuOpen(false);
    setCityMenuOpen(false);
  };

  const handleCityDropdownClick = () => {
    setCityMenuOpen((prev) => !prev);
    setCountryMenuOpen(false);
    setStateMenuOpen(false);
  };

  // Change Handlers
  const handleCountryOnChange = (selectedCountry) => {
    const label = typeof selectedCountry === 'string'
      ? selectedCountry
      : (selectedCountry.label || selectedCountry.value || selectedCountry.country || "");

    if (label !== country) {
      setCountry(label);
      setState('');
      setCity('');
      setStateFromApi([]);
      setCityFromApi([]);
    }
    setCountryMenuOpen(false);
    if (error) setError('');
  };

  const handleStateOnChange = (selectedState) => {
    const label = typeof selectedState === 'string'
      ? selectedState
      : (selectedState.label || selectedState.value || selectedState.state || "");

    if (label !== state) {
      setState(label);
      setCity('');
      setCityFromApi([]);
    }
    setStateMenuOpen(false);
    if (error) setError('');
  };

  const handleCityOnChange = (selectedCity) => {
    const label = typeof selectedCity === 'string'
      ? selectedCity
      : (selectedCity.label || selectedCity.value || selectedCity.city || "");

    setCity(label);
    setCityMenuOpen(false);
    if (error) setError('');
  };

  const handleAddressOnChange = (e) => {
    setAddress(e.target.value);
    if (error) setError('');
  };

  const handlePostalCodeOnChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^a-zA-Z0-9\s-]/g, '');
    if (value.length > 10) {
      value = value.slice(0, 10);
    }
    setPostalCode(value);
    if (error) setError('');
  };

  const handleNextBtnClick = async () => {
    if (isLoading) return;
    if (!country || !state || !city || !address || !postalCode) {
      setError("All fields are required");
      return;
    }
    if (!validatePostalCodeRegex(postalCode, country)) {
      setError("Invalid postal code for the selected country");
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        userId: getUserId(),
        addresses: [
          {
            addressLine: address.trim(),
            city: city.trim(),
            state: state.trim(),
            zipCode: postalCode.trim(),
            country: country.trim()
          }
        ]
      };

      if (signUp) {
        payload.formProgress = 'address completed';
      }

      const data = await updateUser(payload, getToken());
      if (data?.userDetails) {
        dispatch(setUserDetails(data.userDetails));
      }

      if (signUp) {
        dispatch(setActiveComponent("faceRecognition1"));
      } else {
        showSnackbar('Address updated successfully!', 'success');
      }
    } catch (err) {
      console.error("Error updating address:", err);
      setError(err.response?.data?.message || "Something went wrong");
      if (!signUp) {
        showSnackbar(err.response?.data?.message || 'Failed to update address. Please try again.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`EnterAddress ${signUp ? 'signUpMode' : 'editMode'}`}>
      <div className="enterAddressContainer">
        {signUp && <span className="signInText">Sign Up</span>}
        {signUp && (
          <div className="rectangleBoxContainer">
            <div className="rectangleBox"></div>
            <div className="rectangleBox"></div>
            <div className="rectangleBox coloured"></div>
          </div>
        )}

        {/* Country Dropdown */}
        <div className="addressFieldGroup">
          <label htmlFor="country" className="addressLabel">
            Country:
          </label>
          <div className="addressDropdownContainer" ref={countryDropdownRef}>
            <div
              id="country"
              className="addressSelect"
              onClick={handleCountryDrowpdownClick}
            >
              {!country ? (
                <span className="addressPlaceholder">
                  {loadingCountries ? "Loading countries..." : "Select Country"}
                </span>
              ) : (
                <span className="addressInputText">{country}</span>
              )}
              <span className="addressArrowIcon">
                {countryMenuOpen ? (
                  <KeyboardArrowUpRoundedIcon />
                ) : (
                  <KeyboardArrowDownRoundedIcon />
                )}
              </span>
            </div>

            {countryMenuOpen && (
              <ul className="addressDropdownMenu">
                {loadingCountries ? (
                  <li className="dropdownStatusItem">Loading countries...</li>
                ) : countryFromApi.length === 0 ? (
                  <li className="dropdownStatusItem">No countries available</li>
                ) : (
                  countryFromApi.map((c, index) => {
                    const label = typeof c === 'string' ? c : (c.label || c.value || c.country);
                    return (
                      <li key={index} onClick={() => handleCountryOnChange(c)}>
                        {label}
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </div>
        </div>

        {/* State Dropdown */}
        <div className="addressFieldGroup">
          <label htmlFor="state" className="addressLabel">
            Province / State:
          </label>
          <div className="addressDropdownContainer" ref={stateDropdownRef}>
            <div
              id="state"
              className="addressSelect"
              onClick={handleStateDropdownClick}
            >
              {!state ? (
                <span className="addressPlaceholder">
                  {!country
                    ? "Select a country first"
                    : loadingStates
                    ? "Loading states / provinces..."
                    : "Select State"}
                </span>
              ) : (
                <span className="addressInputText">{state}</span>
              )}
              <span className="addressArrowIcon">
                {stateMenuOpen ? (
                  <KeyboardArrowUpRoundedIcon />
                ) : (
                  <KeyboardArrowDownRoundedIcon />
                )}
              </span>
            </div>

            {stateMenuOpen && (
              <ul className="addressDropdownMenu">
                {!country ? (
                  <li className="dropdownStatusItem">Please select a country first</li>
                ) : loadingStates ? (
                  <li className="dropdownStatusItem">Loading states / provinces...</li>
                ) : stateFromApi.length === 0 ? (
                  <li className="dropdownStatusItem">No states found for {country}</li>
                ) : (
                  stateFromApi.map((s, index) => {
                    const label = typeof s === 'string' ? s : (s.label || s.value || s.state);
                    return (
                      <li key={index} onClick={() => handleStateOnChange(s)}>
                        {label}
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </div>
        </div>

        {/* City Dropdown */}
        <div className="addressFieldGroup">
          <label htmlFor="city" className="addressLabel">
            City:
          </label>
          <div className="addressDropdownContainer" ref={cityDropdownRef}>
            <div
              id="city"
              className="addressSelect"
              onClick={handleCityDropdownClick}
            >
              {!city ? (
                <span className="addressPlaceholder">
                  {!state
                    ? "Select a state first"
                    : loadingCities
                    ? "Loading cities..."
                    : "Select City"}
                </span>
              ) : (
                <span className="addressInputText">{city}</span>
              )}
              <span className="addressArrowIcon">
                {cityMenuOpen ? (
                  <KeyboardArrowUpRoundedIcon />
                ) : (
                  <KeyboardArrowDownRoundedIcon />
                )}
              </span>
            </div>

            {cityMenuOpen && (
              <ul className="addressDropdownMenu">
                {!state ? (
                  <li className="dropdownStatusItem">Please select a state first</li>
                ) : loadingCities ? (
                  <li className="dropdownStatusItem">Loading cities...</li>
                ) : cityFromApi.length === 0 ? (
                  <li className="dropdownStatusItem">No cities found for {state}</li>
                ) : (
                  cityFromApi.map((c, index) => {
                    const label = typeof c === 'string' ? c : (c.label || c.value || c.city);
                    return (
                      <li key={index} onClick={() => handleCityOnChange(c)}>
                        {label}
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </div>
        </div>

        {/* Street Address */}
        <div className="addressFieldGroup">
          <label htmlFor="address" className="addressLabel">
            Address:
          </label>
          <div className="addressInputWrapper">
            <input
              id="address"
              className="addressInput"
              type="text"
              placeholder="Enter Address"
              value={address}
              onChange={handleAddressOnChange}
            />
          </div>
        </div>

        {/* Postal Code */}
        <div className="addressFieldGroup">
          <label htmlFor="postalCode" className="addressLabel">
            Postal / Zip Code:
          </label>
          <div className="addressInputWrapper">
            <input
              id="postalCode"
              className="addressInput"
              type="text"
              placeholder="Enter postal code"
              value={postalCode}
              onChange={handlePostalCodeOnChange}
            />
          </div>
          {error && <span className="addressErrorText">{error}</span>}
        </div>

        {/* Community City Transition Notice */}
        {!signUp && userDetails?.addresses?.[0]?.city && city && userDetails.addresses[0].city.trim().toLowerCase() !== city.trim().toLowerCase() && (
          <div className="addressCommunityNotice notice-warning">
            <span className="noticeIcon">⚠️</span>
            <span className="noticeText">
              Changing your city from <strong>{userDetails.addresses[0].city}</strong> to <strong>{city}</strong> will automatically remove you from the <strong>{userDetails.addresses[0].city}</strong> local space and unlock the verified <strong>{city}</strong> local residency community.
            </span>
          </div>
        )}
        {!signUp && !userDetails?.addresses?.[0]?.city && city && (
          <div className="addressCommunityNotice notice-info">
            <span className="noticeIcon">📍</span>
            <span className="noticeText">
              Setting your city to <strong>{city}</strong> will unlock access to the verified <strong>{city}</strong> local residency community.
            </span>
          </div>
        )}

        {/* Submit / Next Button */}
        <div className="addressSubmitBtnContainer">
          <button
            className="addressSubmitBtn"
            onClick={handleNextBtnClick}
            disabled={isLoading}
          >
            {isLoading ? <Loader /> : (signUp ? "Next" : "Submit")}
          </button>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default EnterAddress;
